/**
 * VOICE INTERFACE COMPONENT
 * Voice command processing overlay
 */

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Mic, MicOff } from 'lucide-react';

interface VoiceInterfaceProps {
  sessionId: string;
}

export function VoiceInterface({ sessionId }: VoiceInterfaceProps) {
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState('');

  const toggleListening = () => {
    setIsListening(!isListening);
    if (!isListening) {
      setTranscript('Listening for voice commands...');
    } else {
      setTranscript('');
    }
  };

  return (
    <>
      {/* Voice Button */}
      <motion.button
        onClick={toggleListening}
        className={`fixed bottom-6 right-6 w-14 h-14 rounded-full flex items-center justify-center shadow-lg z-50 ${
          isListening 
            ? 'bg-red-500 hover:bg-red-600' 
            : 'bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700'
        } transition-all`}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
      >
        {isListening ? (
          <MicOff className="w-6 h-6 text-white" />
        ) : (
          <Mic className="w-6 h-6 text-white" />
        )}
      </motion.button>

      {/* Voice Overlay */}
      <AnimatePresence>
        {isListening && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-40"
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              className="bg-white/10 border border-white/20 rounded-lg p-8 backdrop-blur-sm max-w-md w-full mx-4"
            >
              <div className="text-center">
                <motion.div
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  className="w-20 h-20 bg-gradient-to-r from-red-500 to-pink-600 rounded-full flex items-center justify-center mx-auto mb-4"
                >
                  <Mic className="w-10 h-10 text-white" />
                </motion.div>
                
                <h3 className="text-xl font-semibold text-white mb-2">Voice Command Active</h3>
                <p className="text-slate-300 mb-4">Speak your coding request...</p>
                
                <div className="bg-slate-900 border border-slate-700 rounded-lg p-4 mb-4">
                  <p className="text-slate-300 text-sm italic">
                    {transcript || 'Say something like: "Create a React component for user login"'}
                  </p>
                </div>
                
                <button
                  onClick={toggleListening}
                  className="px-6 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                >
                  Stop Listening
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}